import { useMutation, useQuery } from "@apollo/client";

import FlashContext from "components/FlashMessages/FlashContext";
import { Policy, User } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AuthorizationScheme } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { GET_AUTHORIZATION_SCHEME, UPDATE_ACCOUNT_AUTH_SCHEME } from "./gql";
import { humanizeAuthorizationSchemeName } from "./helpers";
import ManagementStrategyItem from "./Item";

type GetAuthorizationSchemeGql = {
  authorizationScheme: AuthorizationScheme;
};

const ManagementStrategy = () => {
  const { accountName } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const { loading, data, error } = useQuery<GetAuthorizationSchemeGql>(GET_AUTHORIZATION_SCHEME);

  const [updateAccountAuthScheme, { loading: changingScheme }] = useMutation(
    UPDATE_ACCOUNT_AUTH_SCHEME,
    {
      refetchQueries: ["GetAuthorizationScheme"],
    }
  );

  useTitle(`Organization Settings · Management strategy · ${accountName}`);

  const ErrorContent = useErrorHandle(error);

  const handleChange = (schema: AuthorizationScheme) => {
    updateAccountAuthScheme({ variables: { schema } })
      .then(({ data: { accountUpdateAuthorizationScheme } }) => {
        reportSuccess({
          message: `Authorization scheme was changed to ${humanizeAuthorizationSchemeName(
            accountUpdateAuthorizationScheme
          )}`,
        });
      })
      .catch(onError);
  };

  const getConfirmationCallback = (authorizationScheme: AuthorizationScheme) => () => {
    handleChange(authorizationScheme);
  };

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading || !data?.authorizationScheme || changingScheme) {
    return <PageLoading />;
  }

  const activeScheme = data.authorizationScheme;

  return (
    <>
      <PageInfoHeader title="Management strategy" />

      <Box direction="column" padding="x-large" gap="x-large" limitWidth="medium">
        <ManagementStrategyItem
          confirmationCallback={getConfirmationCallback(AuthorizationScheme.LoginPolicies)}
          confirmationDescription={
            <>
              After enabling login policy feature, all active sessions (except the current one) will
              be invalidated. Only API keys with root admin access will continue to work. Non root
              admin keys will need to be defined in the login policy.
              <br />
              <br />
              You will be still able to edit and activate user management again.
            </>
          }
          active={activeScheme === AuthorizationScheme.LoginPolicies}
          title="Login policy"
          icon={Policy}
        >
          Use login policies for full control over your login process. This allows you to support
          very advanced use-cases.
        </ManagementStrategyItem>

        <ManagementStrategyItem
          confirmationCallback={getConfirmationCallback(AuthorizationScheme.ManagedUsers)}
          confirmationDescription={
            <>
              After enabling the identity access management feature, all active sessions (except the
              current one) will be invalidated. Only API keys with root admin access will continue
              to work.
              <br />
              <br />
              You will be still able to edit and re-activate your existing login policy.
            </>
          }
          active={activeScheme === AuthorizationScheme.ManagedUsers}
          title="Identity Access Management"
          icon={User}
        >
          Manage users, groups (mapped from your IdP), API keys, and custom roles directly in the
          UI. No code required, with clear visibility and control at scale.
        </ManagementStrategyItem>
      </Box>
    </>
  );
};

export default ManagementStrategy;
